import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
} from 'chart.js';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip);

// ---------------------------------------------------------------------------
// GuidedEntryRunChart — compact "Run chart" of the values entered so far in a
// GuidedEntry session. Shown only at >= tablet widths where there is room
// (see GuidedEntry.js).
//
// This is a RAW run chart: points plotted in entry order. It deliberately
// does NOT compute or imply control limits / a centerline from partial
// in-progress data — that would misrepresent an incomplete sample as a
// stable process. The only reference lines drawn are the caller-supplied
// spec limits (USL/LSL/Target), when present.
//
// Props
// -----
// values      Array<number>              Entered numeric values, in order.
// specLimits  {usl,lsl,target,            Optional reference lines. uslLabel/
//              uslLabel?,lslLabel?,       lslLabel?/targetLabel? override the
//              targetLabel?}|null         tooltip labels (default 'USL'/'LSL'/
//                                         'Target') — callers whose lines are
//                                         NOT spec limits (e.g. Type 1 GRR's
//                                         Minitab Ref +/- k*Tol/2 band) should
//                                         pass their own labels.
// ---------------------------------------------------------------------------
const toFiniteOrNull = (v) => {
    if (v === undefined || v === null || v === '') return null;
    const num = Number(v);
    return Number.isFinite(num) ? num : null;
};

function GuidedEntryRunChart({ values, specLimits }) {
    const usl = toFiniteOrNull(specLimits?.usl);
    const lsl = toFiniteOrNull(specLimits?.lsl);
    const target = toFiniteOrNull(specLimits?.target);
    const uslLabel = specLimits?.uslLabel ?? 'USL';
    const lslLabel = specLimits?.lslLabel ?? 'LSL';
    const targetLabel = specLimits?.targetLabel ?? 'Target';

    // Memoized so Chart.js only re-lays-out when a value is recorded or a spec
    // limit changes — not on parent re-renders (this component is also wrapped in
    // React.memo below; GuidedEntry memoizes the values array it passes in).
    // Labels join the dep array as primitives (strings), same as the numeric
    // limits, so a label-only change still triggers a recompute.
    const data = useMemo(() => {
        const n = values.length;
        const refLine = (value, color, label) => (value === null ? null : {
            label,
            data: new Array(n).fill(value),
            borderColor: color,
            borderDash: [6, 4],
            borderWidth: 1.5,
            pointRadius: 0,
            pointHoverRadius: 0,
            tension: 0,
        });
        return {
            labels: values.map((_, i) => `${i + 1}`),
            datasets: [
                {
                    label: 'Value',
                    data: values,
                    borderColor: '#90caf9',
                    backgroundColor: '#90caf9',
                    pointRadius: 3,
                    pointHoverRadius: 4,
                    borderWidth: 2,
                    tension: 0,
                },
                refLine(usl, '#ef5350', uslLabel),
                refLine(lsl, '#ef5350', lslLabel),
                refLine(target, '#81c784', targetLabel),
            ].filter(Boolean),
        };
    }, [values, usl, lsl, target, uslLabel, lslLabel, targetLabel]);

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: { mode: 'nearest', intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: { enabled: true },
        },
        scales: {
            x: {
                ticks: { color: 'rgba(255,255,255,0.4)', maxTicksLimit: 8 },
                grid: { color: 'rgba(255,255,255,0.06)' },
            },
            y: {
                ticks: { color: 'rgba(255,255,255,0.4)' },
                grid: { color: 'rgba(255,255,255,0.06)' },
            },
        },
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 560, mx: 'auto', px: 2 }}>
            <Typography
                variant="caption"
                sx={{ color: 'rgba(255,255,255,0.4)', display: 'block', mb: 0.5, textAlign: 'center', letterSpacing: 0.5 }}
            >
                Run chart
            </Typography>
            <Box sx={{ height: 180 }}>
                <Line data={data} options={options} />
            </Box>
        </Box>
    );
}

// React.memo: skip re-renders entirely while props are stable (GuidedEntry
// re-renders per keystroke, but values/specLimits only change per recorded value).
export default React.memo(GuidedEntryRunChart);
